import { apiFetch } from './http';

export type ChangelogKind = 'NEW' | 'BETTER' | 'FIXED' | 'GONE';

export interface ChangelogEntry {
  key: string;
  kind: ChangelogKind;
  /** The bold half: four or five words. */
  title: string;
  /** The sentence after it; empty when the title said everything. */
  text: string;
}

export interface ChangelogRelease {
  /** `v0.10.0`, or `unreleased` for what has landed and has no version yet. */
  version: string;
  /** The day of the tag; null while unreleased. */
  date: string | null;
  unreleased: boolean;
  entries: ChangelogEntry[];
}

/** Public: no account, no session. The one page a visitor can read to see whether anything is happening. */
export const fetchChangelog = () => apiFetch<ChangelogRelease[]>('/api/changelog');

// --- what only an admin sees ----------------------------------------------------------------

export interface AdminChangelogEntry extends ChangelogEntry {
  /** When this line went to Discord on its own; null when it never did. */
  publishedAt: string | null;
}

export interface AdminChangelogRelease {
  version: string;
  date: string | null;
  unreleased: boolean;
  /** When the whole block was announced; null when it was not. */
  publishedAt: string | null;
  /** Recorded as known on the first run, without ever being posted. */
  seeded: boolean;
  entries: AdminChangelogEntry[];
}

export const fetchAdminChangelog = () => apiFetch<AdminChangelogRelease[]>('/api/admin/changelog');

/**
 * What Discord answered, kept whole. A refusal is never "it did not work":
 * the token, the invitation and the channel's permissions fail in three
 * different places and Discord says which every time.
 */
export interface DiscordRefusal {
  sent: boolean;
  messageId: string | null;
  /** The HTTP status, or 0 when Discord never answered at all. */
  status: number;
  /** Discord's own code (50013 and its kind), or 0 when it sent none. */
  code: number;
  /** Discord's own sentence, in English; the fallback when the code is one we do not name. */
  text: string | null;
}

/** `itemKey` is a version (`v0.10.0`) or one line of it (`v0.10.0#1a2b3c4d`). */
export const publishChangelog = (itemKey: string) =>
  apiFetch<{ sent: boolean; reason: string; discord: DiscordRefusal | null }>(
    '/api/admin/changelog/publish',
    { method: 'POST', body: { itemKey } },
  );

// --- the bot --------------------------------------------------------------------------------

export type ChangelogMode = 'RELEASE' | 'ENTRY' | 'MANUAL';

export interface DiscordBotSettings {
  botEnabled: boolean;
  /** The token is never read back; this says whether there is one. */
  botTokenSet: boolean;
  sessionsChannelId: string;
  votesChannelId: string;
  changelogChannelId: string;
  changelogEnabled: boolean;
  changelogMode: ChangelogMode;
  changelogMentionRoleId: string;
  /** The test sprint's channel and the application's public key (not a secret); empty means no tasks are announced and no buttons trusted. */
  provesChannelId: string;
  publicKey: string;
}

export interface DiscordBotView {
  settings: DiscordBotSettings;
  /** Per channel, which of the two ways it would actually use now. */
  transports: Record<string, 'bot' | 'webhook' | 'none'>;
}

export const fetchDiscordBot = () => apiFetch<DiscordBotView>('/api/admin/discord');

export const updateDiscordBot = (change: {
  botEnabled?: boolean;
  botToken?: string;
  sessionsChannelId?: string;
  votesChannelId?: string;
  changelogChannelId?: string;
  changelogEnabled?: boolean;
  changelogMode?: ChangelogMode;
  changelogMentionRoleId?: string;
  provesChannelId?: string;
  publicKey?: string;
}) => apiFetch<DiscordBotView>('/api/admin/discord', { method: 'PUT', body: change });

/** Asks Discord who the token belongs to, without posting anything anywhere. */
export const testDiscordIdentity = () =>
  apiFetch<{ configured: boolean; name: string | null }>('/api/admin/discord/identity', {
    method: 'POST',
  });
